import { useCallback, useEffect, useMemo, useState } from "react";
import type { EngagementProvider } from "@/provider/EngagementProvider";
import type {
	EngagementProviderBootInput,
	ProviderRuntimeSnapshot,
} from "@/provider/ProviderTypes";
import { getProviderRuntimeStore } from "@/provider/ProviderRuntimeStore";
import { useProviderRuntimeSnapshot } from "@/hooks/useProviderRuntimeSnapshot";
import { useCaptainUpBoot } from "@/hooks/useCaptainUpBoot";
import { debugLog, debugWarn } from "@/utils/debug";
import {
	assertEligibleRuntimeInputs,
	isRuntimeReadyForProvider,
	isSupportedProviderId,
	markRuntimeBooting,
	markRuntimeEligible,
	markRuntimeFailed,
	markRuntimeReady,
	markUnsupportedProviderDegraded,
	normalizeBootInput,
	normalizeIntegrationType,
	shouldMarkBooting,
	shouldMarkFailed,
	shouldMarkReady,
	shouldResetRuntime,
} from "@/provider/ProviderSupervisor";

export type ProviderRuntimeHookState = {
	provider: EngagementProvider | null;
	providerReady: boolean;
	providerRuntimeSnapshot: ProviderRuntimeSnapshot;
	activeBootGeneration: number;
	readyBootGeneration: number | null;
	inFlightBootGeneration: number | null;
	requestFreshProviderBoot: () => void;
};

const CAPTAINUP_PROVIDER_ID = "captainup";

export function useProviderRuntime(
	providerBootEligible: boolean,
	providerBootInput: EngagementProviderBootInput | null
): ProviderRuntimeHookState {
	const runtimeStore = getProviderRuntimeStore();
	const providerRuntimeSnapshot = useProviderRuntimeSnapshot();

	const [requestedBootGeneration, setRequestedBootGeneration] = useState(0);

	const integrationType = normalizeIntegrationType(providerBootInput);

	const normalizedBootInput = useMemo(
		() =>
			normalizeBootInput(
				providerBootEligible,
				providerBootInput,
				integrationType
			),
		[providerBootEligible, providerBootInput, integrationType]
	);

	const isSupportedProvider = isSupportedProviderId(integrationType);
	const isCaptainUpProvider = integrationType === CAPTAINUP_PROVIDER_ID;

	const requestFreshProviderBoot = useCallback(() => {
		setRequestedBootGeneration((prev) => {
			const next = prev + 1;

			debugLog("[EF ProviderRuntime] fresh provider boot requested", {
				currentBootGeneration: prev,
				nextBootGeneration: next,
				providerBootEligible,
				integrationType,
				hasNormalizedBootInput: !!normalizedBootInput,
				runtimePhase: runtimeStore.getSnapshot().phase,
			});

			return next;
		});
	}, [integrationType, normalizedBootInput, providerBootEligible, runtimeStore]);

	const captainUpBootState = useCaptainUpBoot(
		providerBootEligible && isCaptainUpProvider,
		providerBootEligible && isCaptainUpProvider ? normalizedBootInput : null,
		requestedBootGeneration
	);

	useEffect(() => {
		if (
			shouldResetRuntime(
				providerBootEligible,
				normalizedBootInput,
				integrationType
			)
		) {
			debugLog(
				"[EF ProviderRuntime] resetting runtime because provider boot prerequisites are absent",
				{
					providerBootEligible,
					hasProviderBootInput: !!providerBootInput,
					integrationType,
					requestedBootGeneration,
				}
			);

			runtimeStore.reset();
			return;
		}

		const resolvedIntegrationType = integrationType;

		if (!resolvedIntegrationType) {
			throw new Error(
				"[EF ProviderRuntime] expected integrationType to exist after prerequisite guard"
			);
		}

		assertEligibleRuntimeInputs(normalizedBootInput, resolvedIntegrationType);

		markRuntimeEligible(
			runtimeStore,
			resolvedIntegrationType,
			normalizedBootInput,
			requestedBootGeneration
		);

		debugLog("[EF ProviderRuntime] runtime marked eligible", {
			integrationType: resolvedIntegrationType,
			requestedBootGeneration,
			runtimePhase: runtimeStore.getSnapshot().phase,
		});
	}, [
		integrationType,
		normalizedBootInput,
		providerBootEligible,
		providerBootInput,
		requestedBootGeneration,
		runtimeStore,
	]);

	useEffect(() => {
		if (!providerBootEligible || !normalizedBootInput || !integrationType) {
			return;
		}

		if (isSupportedProvider) {
			return;
		}

		debugWarn("[EF ProviderRuntime] unsupported provider integration type", {
			integrationType,
			requestedBootGeneration,
		});

		markUnsupportedProviderDegraded(runtimeStore);
	}, [
		integrationType,
		isSupportedProvider,
		normalizedBootInput,
		providerBootEligible,
		requestedBootGeneration,
		runtimeStore,
	]);

	useEffect(() => {
		const snapshot = runtimeStore.getSnapshot();

		if (
			!shouldMarkBooting(
				isCaptainUpProvider,
				!!normalizedBootInput,
				captainUpBootState.activeBootGeneration,
				captainUpBootState.inFlightBootGeneration,
				requestedBootGeneration,
				snapshot,
				CAPTAINUP_PROVIDER_ID
			)
		) {
			return;
		}

		debugLog("[EF ProviderRuntime] runtime marked booting", {
			requestedBootGeneration,
			previousPhase: snapshot.phase,
		});

		markRuntimeBooting(runtimeStore, requestedBootGeneration);
	}, [
		captainUpBootState.activeBootGeneration,
		captainUpBootState.inFlightBootGeneration,
		isCaptainUpProvider,
		normalizedBootInput,
		requestedBootGeneration,
		runtimeStore,
	]);

	useEffect(() => {
		const snapshot = runtimeStore.getSnapshot();
		const currentRuntimeProvider = runtimeStore.getProvider();

		if (
			!shouldMarkReady(
				isCaptainUpProvider,
				!!normalizedBootInput,
				captainUpBootState.captainUpProvider,
				captainUpBootState.captainUpReady,
				captainUpBootState.readyBootGeneration,
				requestedBootGeneration,
				snapshot,
				currentRuntimeProvider,
				CAPTAINUP_PROVIDER_ID
			)
		) {
			return;
		}

		debugLog("[EF ProviderRuntime] runtime marked ready", {
			requestedBootGeneration,
			previousPhase: snapshot.phase,
		});

		markRuntimeReady(
			runtimeStore,
			captainUpBootState.captainUpProvider as EngagementProvider,
			requestedBootGeneration
		);
	}, [
		captainUpBootState.captainUpProvider,
		captainUpBootState.captainUpReady,
		captainUpBootState.readyBootGeneration,
		isCaptainUpProvider,
		normalizedBootInput,
		requestedBootGeneration,
		runtimeStore,
	]);

	useEffect(() => {
		const snapshot = runtimeStore.getSnapshot();

		if (
			!shouldMarkFailed(
				isCaptainUpProvider,
				!!normalizedBootInput,
				captainUpBootState.lastBootError,
				captainUpBootState.inFlightBootGeneration,
				snapshot,
				CAPTAINUP_PROVIDER_ID
			)
		) {
			return;
		}

		debugLog("[EF ProviderRuntime] runtime marked failed", {
			requestedBootGeneration,
			error: captainUpBootState.lastBootError?.message ?? null,
			previousPhase: snapshot.phase,
		});

		markRuntimeFailed(
			runtimeStore,
			captainUpBootState.lastBootError as Error
		);
	}, [
		captainUpBootState.inFlightBootGeneration,
		captainUpBootState.lastBootError,
		isCaptainUpProvider,
		normalizedBootInput,
		requestedBootGeneration,
		runtimeStore,
	]);

	if (isCaptainUpProvider) {
		return {
			provider: captainUpBootState.captainUpProvider,
			providerReady: isRuntimeReadyForProvider(
				providerRuntimeSnapshot,
				CAPTAINUP_PROVIDER_ID,
				captainUpBootState.captainUpReady
			),
			providerRuntimeSnapshot,
			activeBootGeneration: captainUpBootState.activeBootGeneration,
			readyBootGeneration: captainUpBootState.readyBootGeneration,
			inFlightBootGeneration: captainUpBootState.inFlightBootGeneration,
			requestFreshProviderBoot,
		};
	}

	return {
		provider: null,
		providerReady: false,
		providerRuntimeSnapshot,
		activeBootGeneration: requestedBootGeneration,
		readyBootGeneration: null,
		inFlightBootGeneration: null,
		requestFreshProviderBoot,
	};
}

export default useProviderRuntime;